import React from "react";
import { NavItemComponent } from "../../services/types/ComponentTypes";

interface NavItemProps {
  config: NavItemComponent;
  isActive?: boolean;
  onClick?: (route?: string, action?: string) => void;
}

export const NavItem: React.FC<NavItemProps> = ({
  config,
  isActive = false,
  onClick,
}) => {
  const [isHovered, setIsHovered] = React.useState(false);

  const handleClick = () => {
    if (config.disabled) return;

    if (onClick) {
      onClick(config.route, config.onPressAction);
    }
  };

  const baseStyles: React.CSSProperties = {
    padding: config.style?.padding || 6,
    margin: config.style?.margin || 2,
    backgroundColor: isActive
      ? "rgba(255, 255, 255, 0.1)"
      : config.style?.backgroundColor || "transparent",
    color: config.style?.textColor || "#FFFFFF",
    border: config.style?.borderWidth
      ? `${config.style.borderWidth}px solid ${
          config.style.borderColor || "transparent"
        }`
      : "none",
    borderRadius: config.style?.borderRadius || 8,
    fontSize: config.style?.fontSize || 12,
    fontWeight: config.style?.fontWeight || "normal",
  };

  const hoverBgClass = isActive ? "bg-white/15" : "bg-white/8";

  const iconSize = config.style?.fontSize ? config.style.fontSize * 1.2 : 16;
  const labelFontWeight = isActive ? "600" : config.style?.fontWeight || "500";

  // Check if icon is an image path (starts with / or http, or contains image extensions)
  const isImageIcon =
    config.icon &&
    typeof config.icon === "string" &&
    (config.icon.startsWith("/") ||
      config.icon.startsWith("http") ||
      /\.(webp|png|jpg|jpeg|svg|gif)$/i.test(config.icon));

  // Render icon based on type
  const renderIcon = () => {
    if (!config.icon) return null;
    
    if (isImageIcon) {
      return (
        <img
          src={config.icon}
          alt={config.label || "icon"}
          className="w-4 h-4"
          style={{
            objectFit: "contain",
          }}
        />
      );
    }
    
    // Check if it's an SVG path (starts with <svg or contains SVG-like content)
    if (typeof config.icon === "string" && config.icon.trim().startsWith("<svg")) {
      return (
        <span 
          className="w-4 h-4 flex items-center justify-center"
          dangerouslySetInnerHTML={{ __html: config.icon }}
        />
      );
    }
    
    // For emoji or text icons, use smaller size
    return (
      <span className="text-sm flex items-center justify-center w-4 h-4">
        {config.icon}
      </span>
    );
  };

  return (
    <a
      href="#"
      className={`flex items-center px-4 py-2 text-xs text-gray-300 dark:text-gray-300 hover:bg-gray-700/50 dark:hover:bg-gray-700/50 hover:text-white dark:hover:text-white transition-all duration-200 rounded-md mx-1 ${
        config.disabled ? "cursor-not-allowed opacity-70" : "cursor-pointer"
      } ${isActive ? "text-white dark:text-white bg-gray-700/80 dark:bg-gray-700/80 shadow-sm" : ""}`}
      onClick={(e) => {
        e.preventDefault();
        handleClick();
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      title={config.tooltip || config.label}
    >
      {config.icon && (
        <span className="w-4 h-4 mr-2.5 flex items-center justify-center flex-shrink-0">
          {renderIcon()}
        </span>
      )}
      <span className="flex-1 text-xs">
        {config.label}
      </span>
    </a>
  );
};
